import React from 'react';
import { i18n } from 'config/locales';

import { ForeignBusinessPoliticalItemModel } from 'forms/sections/foreign/business/political/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import Field from 'components/Form/Field';
import DateRange from 'components/Form/DateRange';
import Country from 'components/Form/Country';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';
import Branch from 'components/Form/Branch';

interface Props extends Partial<ForeignBusinessPoliticalItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
  formType?: string
}

export default class PoliticalItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  update = (queue: Partial<ForeignBusinessPoliticalItemModel>) => {
    this.props.onUpdate({
      Position: this.props.Position,
      Dates: this.props.Dates,
      Country: this.props.Country,
      Reason: this.props.Reason,
      Eligibility: this.props.Eligibility,
      CountryFocused: this.props.CountryFocused,
      CurrentlyEligible: this.props.CurrentlyEligible,
      CurrentlyEligibleExplanation: this.props.CurrentlyEligibleExplanation,
      ...queue,
    });
  };

  updatePosition = (values: ForeignBusinessPoliticalItemModel['Position']) => {
    this.update({
      Position: values,
    });
  };

  updateDates = (values: ForeignBusinessPoliticalItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateCountry = (values: ForeignBusinessPoliticalItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateReason = (values: ForeignBusinessPoliticalItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateEligibility = (values: ForeignBusinessPoliticalItemModel['Eligibility']) => {
    this.update({
      Eligibility: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  updateCurrentlyEligible = (values: ForeignBusinessPoliticalItemModel['CurrentlyEligible']) => {
    this.update({
      CurrentlyEligible: values,
      CurrentlyEligibleExplanation: values?.value === 'Yes' ? this.props.CurrentlyEligibleExplanation : {},
    });
  };

  updateCurrentlyEligibleExplanation = (values: ForeignBusinessPoliticalItemModel['CurrentlyEligibleExplanation']) => {
    this.update({
      CurrentlyEligibleExplanation: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className="foreign-business-political-item">
        <Field
          title={i18n.t('foreign.pvq.business.political.heading.position')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Position`}
        >
          <Text
            name="Position"
            {...this.props.Position}
            onUpdate={this.updatePosition}
            onError={this.props.onError}
            className="foreign-business-political-position"
            required={this.props.required}
            attribute={`${attribute}.Position`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.political.heading.dates')}
          help="foreign.pvq.business.political.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            {...this.props.Dates}
            onUpdate={this.updateDates}
            onError={this.props.onError}
            minDateEqualTo
            className="foreign-business-political-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.political.heading.country')}
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            className="foreign-business-political-country"
            required={this.props.required}
            attribute={`${attribute}.Country`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.political.heading.reason')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Reason`}
        >
          <Textarea
            name="Reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            className="foreign-business-political-reason"
            required={this.props.required}
            attribute={`${attribute}.Reason`}
          />
        </Field>

        <Branch
          {...this.props.CurrentlyEligible}
          name="CurrentlyEligible"
          attribute={`${attribute}.CurrentlyEligible`}
          label={i18n.t('foreign.pvq.business.political.heading.eligibility')}
          labelSize="h4"
          className="currently-eligible"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateCurrentlyEligible}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.CurrentlyEligible?.value === 'Yes' && (
        <Field
          title={i18n.t('foreign.pvq.business.political.heading.explanation')}
          attribute={`${attribute}.CurrentlyEligibleExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.CurrentlyEligibleExplanation}
            name={`${attribute}.CurrentlyEligibleExplanation`}
            attribute="CurrentlyEligibleExplanation"
            className="currently-eligible-explanation"
            onUpdate={this.updateCurrentlyEligibleExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}
      </div>
    ) : (
      <div className="foreign-business-political-item">
        <Field
          title={i18n.t('foreign.business.political.heading.position')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Position`}
        >
          <Text
            name="Position"
            {...this.props.Position}
            onUpdate={this.updatePosition}
            onError={this.props.onError}
            className="foreign-business-political-position"
            required={this.props.required}
            attribute={`${attribute}.Position`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.political.heading.dates')}
          help="foreign.business.political.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            {...this.props.Dates}
            onUpdate={this.updateDates}
            onError={this.props.onError}
            minDateEqualTo
            className="foreign-business-political-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.political.heading.country')}
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            className="foreign-business-political-country"
            required={this.props.required}
            attribute={`${attribute}.Country`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.political.heading.reason')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Reason`}
        >
          <Textarea
            name="Reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            className="foreign-business-political-reason"
            required={this.props.required}
            attribute={`${attribute}.Reason`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.political.heading.eligibility')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Eligibility`}
        >
          <Text
            name="Eligibility"
            {...this.props.Eligibility}
            onUpdate={this.updateEligibility}
            onError={this.props.onError}
            className="foreign-business-political-eligibility"
            required={this.props.required}
            attribute={`${attribute}.Eligibility`}
          />
        </Field>
      </div>
    );
  }
}

PoliticalItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  CountryFocused: false,
  CurrentlyEligible: {},
  CurrentlyEligibleExplanation: {},
};
